import { useState, useMemo } from 'react'
import { LayoutGroup, AnimatePresence, motion } from 'framer-motion'
import { Shard, KanbanColumn, STATUS_TO_COLUMN } from '../types/shard'
import { KanbanCard } from './KanbanCard'
import { Worker } from '../hooks/useWorkers'
import { getPersonaTier } from '../constants/personas'

interface KanbanBoardProps {
  shards: Shard[]
  onCardClick: (shard: Shard) => void
  workers: Worker[]
}

const COLUMNS: KanbanColumn[] = ['Backlog', 'Blocked', 'In Progress', 'Review', 'Done']

type GroupMode = 'none' | 'assignee'

const TIER_LABELS: Record<number, string> = {
  0: 'Unassigned',
  1: 'Orchestration',
  2: 'Planning & Review',
  3: 'Implementation',
}

function getShardPersona(shard: Shard, workers: Worker[]): string | undefined {
  const worker = shard.assigned_worker
    ? workers.find(w => w.name === shard.assigned_worker)
    : shard.assignee
      ? workers.find(w => w.name === shard.assignee)
      : null
  return worker?.persona || shard.assignee?.trim() || undefined
}

export function KanbanBoard({ shards, onCardClick, workers }: KanbanBoardProps) {
  const [expandedEmpty, setExpandedEmpty] = useState<Set<string>>(new Set())
  const [groupMode, setGroupMode] = useState<GroupMode>('none')
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches

  // Sort shards ascending by numeric ID, then group by column
  const sortedShards = useMemo(() => [...shards].sort((a, b) => {
    const numA = parseInt(a.id.replace(/\D+/g, ''), 10) || 0
    const numB = parseInt(b.id.replace(/\D+/g, ''), 10) || 0
    return numA - numB
  }), [shards])

  const shardsByColumn = useMemo(() => sortedShards.reduce((acc, shard) => {
    const column = STATUS_TO_COLUMN[shard.status]
    if (!acc[column]) acc[column] = []
    acc[column].push(shard)
    return acc
  }, {} as Record<KanbanColumn, Shard[]>), [sortedShards])

  const groupByTier = (columnShards: Shard[]): { tier: number; label: string; shards: Shard[] }[] => {
    const groups: Record<number, Shard[]> = {}
    for (const shard of columnShards) {
      const persona = getShardPersona(shard, workers)
      const tier = persona ? getPersonaTier(persona) : 0
      if (!groups[tier]) groups[tier] = []
      groups[tier].push(shard)
    }
    return Object.entries(groups)
      .map(([t, s]) => ({ tier: Number(t), label: TIER_LABELS[Number(t)] || 'Other', shards: s }))
      .sort((a, b) => a.tier - b.tier)
  }

  return (
    <LayoutGroup>
      {/* Group toggle */}
      <div className="flex items-center gap-2 px-6 pt-4 pb-0">
        <span className="text-xs text-gray-500">Group by:</span>
        <button
          onClick={() => setGroupMode(groupMode === 'none' ? 'assignee' : 'none')}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            groupMode === 'assignee'
              ? 'bg-cosmic-nebula/20 text-cosmic-star'
              : 'bg-white/5 text-gray-400 hover:text-gray-300'
          }`}
        >
          Assignee Type
        </button>
      </div>

      <div className="flex gap-4 h-full p-6 overflow-x-auto min-h-0 max-lg:flex-col max-lg:p-4 max-lg:overflow-y-auto max-lg:overflow-x-hidden">
        {COLUMNS.map((column) => {
          const columnShards = shardsByColumn[column] || []
          const isCollapsedOnMobile = isMobile && columnShards.length === 0 && !expandedEmpty.has(column)

          if (isCollapsedOnMobile) {
            return (
              <button
                key={column}
                onClick={() => setExpandedEmpty(prev => new Set(prev).add(column))}
                className="flex items-center justify-between px-4 py-2 border-b border-cosmic-border/30 text-sm flex-shrink-0"
              >
                <span className="font-semibold text-gray-400">{column}</span>
                <span className="text-xs text-gray-600">0 tasks</span>
              </button>
            )
          }

          const tierGroups = groupMode === 'assignee' ? groupByTier(columnShards) : null

          return (
            <div
              key={column}
              role="region"
              aria-label={`${column} column`}
              className="flex-1 min-w-[280px] flex flex-col min-h-0"
            >
              {/* Column Header */}
              <div className="mb-4 pb-2 border-b border-cosmic-border flex-shrink-0">
                <h2 className="text-lg font-semibold text-white">{column}</h2>
                <span className="text-xs text-gray-400">
                  {columnShards.length} {columnShards.length === 1 ? 'task' : 'tasks'}
                </span>
              </div>

              {/* Column Content */}
              <div className="flex-1 space-y-3 overflow-y-auto min-h-0">
                <AnimatePresence mode="popLayout">
                  {columnShards.length === 0 ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-8 text-gray-500 text-sm"
                    >
                      No tasks
                    </motion.div>
                  ) : tierGroups ? (
                    tierGroups.map(({ tier, label, shards: groupShards }) => (
                      <div key={tier}>
                        <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1 mt-2 first:mt-0">
                          {label}
                        </div>
                        {groupShards.map((shard) => (
                          <div key={shard.id} className="mb-3">
                            <KanbanCard shard={shard} onClick={onCardClick} workers={workers} allShards={shards} />
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    columnShards.map((shard) => (
                      <KanbanCard key={shard.id} shard={shard} onClick={onCardClick} workers={workers} allShards={shards} />
                    ))
                  )}
                </AnimatePresence>
              </div>
            </div>
          )
        })}
      </div>
    </LayoutGroup>
  )
}
